'use client'

import type { Media, Test, TestSubmission } from '@/payload-types'
import type { AnswerObject, GradingDetail } from '@/types/answer.types'
import type { DefaultNodeTypes } from '@payloadcms/richtext-lexical'
import type { JSXConvertersFunction } from '@payloadcms/richtext-lexical/react'
import { RichText } from '@payloadcms/richtext-lexical/react'
import { LoadingOverlay, toast, useDocumentInfo, useField } from '@payloadcms/ui'
import { useEffect, useState } from 'react'
import { QuestionGrader } from './QuestionGrader'
import type { TestSubmissionGraderProps } from './types'

// Custom JSX converters to handle upload nodes (images)
const richTextConverters: JSXConvertersFunction<DefaultNodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  upload: ({ node }) => {
    // The media data can be in node.value (when populated) or node.fields (Lexical structure)
    const media = (typeof node.value === 'object' ? node.value : node.fields) as Media | undefined
    if (!media || typeof media !== 'object') {
      return null
    }

    const url = media.url || media.fullUrl
    const { alt, width, height } = media

    if (!url) {
      return null
    }

    return (
      <img
        src={url}
        alt={alt || ''}
        width={width || undefined}
        height={height || undefined}
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    )
  },
})

export const TestSubmissionGrader: React.FC<TestSubmissionGraderProps> = ({
  submission,
  test,
  onScoreUpdate,
  submissionId,
}) => {
  const [feedback, setFeedback] = useState<{ [key: string]: string }>({})
  const [score, setScore] = useState(submission.score || 0)
  const [maxScore, setMaxScore] = useState(submission.maxScore || 0)
  const [gradingDetails, setGradingDetails] = useState<GradingDetail[]>([])

  // Initialize grading details from submission
  useEffect(() => {
    let details: GradingDetail[] = []

    if (submission.gradingDetails) {
      // Check if it's already an array
      if (Array.isArray(submission.gradingDetails)) {
        details = submission.gradingDetails as GradingDetail[]
      }
      // Handle nested format: { gradingDetails: [...] }
      else if (
        typeof submission.gradingDetails === 'object' &&
        'gradingDetails' in submission.gradingDetails
      ) {
        const nested = submission.gradingDetails as any
        details = Array.isArray(nested.gradingDetails) ? nested.gradingDetails : []
      }
    }

    setGradingDetails(details)
  }, [submission.id])

  const handleManualGrade = async (
    questionId: string,
    isCorrect: boolean,
    points: number,
    feedbackText?: string,
  ) => {
    const gradePromise = fetch(`/api/test-submissions/grade-question/${submissionId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        questionId,
        isCorrect,
        points,
        feedback: feedbackText,
      }),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }
      return response.json()
    })

    toast.promise(gradePromise, {
      loading: 'Grading...',
      success: (result) => {
        // Calculate passed status
        const passed = result.submission.percentageScore >= test.minimumPercentage

        // Update parent component with all values including passed
        onScoreUpdate(
          result.submission.score,
          result.submission.maxScore,
          result.submission.percentageScore,
          result.submission.gradingDetails,
          passed,
        )

        // Update local state
        setScore(result.submission.score)
        setMaxScore(result.submission.maxScore)
        setGradingDetails(result.submission.gradingDetails)

        return 'Question graded and saved successfully'
      },
      error: (error) => {
        console.error('Error grading question:', error)
        return `Failed to grade question: ${error instanceof Error ? error.message : 'Unknown error'}`
      },
    })
  }

  const handleFeedbackChange = (questionId: string, feedbackText: string) => {
    setFeedback((prev) => ({ ...prev, [questionId]: feedbackText }))
  }

  if (!test.questions || !submission.answers) {
    return (
      <div className="p-4 text-center text-[var(--theme-elevation-400)]">
        <p>No test data available</p>
      </div>
    )
  }

  // Create grading map from state
  const gradingMap = new Map<string, GradingDetail>()
  gradingDetails.forEach((detail) => {
    if (detail.questionId) {
      gradingMap.set(detail.questionId, detail)
    }
  })

  // Calculate grading progress
  const totalQuestions = test.questions?.length || 0
  const gradedQuestions = gradingDetails.filter(
    (detail) => detail.isCorrect !== null && detail.isCorrect !== undefined,
  ).length
  const needsGradingCount = totalQuestions - gradedQuestions
  const progressPercent =
    totalQuestions > 0 ? Math.round((gradedQuestions / totalQuestions) * 100) : 0
  const percentageScore = submission.percentageScore || 0
  const isPassed = percentageScore >= test.minimumPercentage

  return (
    <div className="space-y-6">
      {/* Grading Progress Banner */}
      {needsGradingCount > 0 && (
        <div
          className="p-4 border-2 rounded-lg"
          style={{
            borderColor: 'var(--theme-warning-200)',
            backgroundColor: 'var(--theme-warning-50)',
          }}
        >
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <div className="font-bold" style={{ color: 'var(--theme-warning-800)' }}>
                  {needsGradingCount}{' '}
                  {needsGradingCount === 1 ? 'Question Needs' : 'Questions Need'} Grading
                </div>
                <div className="text-sm" style={{ color: 'var(--theme-warning-700)' }}>
                  {gradedQuestions} of {totalQuestions} questions graded
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold" style={{ color: 'var(--theme-warning-700)' }}>
                {progressPercent}%
              </div>
              <div className="text-xs" style={{ color: 'var(--theme-warning-700)' }}>
                Complete
              </div>
            </div>
          </div>
          {/* Progress bar */}
          <div
            className="w-full h-3 rounded-full overflow-hidden"
            style={{ backgroundColor: 'var(--theme-warning-200)' }}
          >
            <div
              className="h-full transition-all duration-300 rounded-full"
              style={{
                width: `${progressPercent}%`,
                backgroundColor: 'var(--theme-warning-600)',
              }}
            />
          </div>
        </div>
      )}

      {/* Grading Summary */}
      <div
        className="p-6 border-2 rounded-lg"
        style={{
          backgroundColor: 'var(--theme-elevation-50)',
          borderColor: 'var(--theme-elevation-200)',
        }}
      >
        <h3 className="mb-6 text-xl font-bold" style={{ color: 'var(--theme-text)' }}>
          Grading Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-4xl font-bold" style={{ color: 'var(--theme-success-700)' }}>
              {score}
            </div>
            <div
              className="mt-2 text-sm font-medium"
              style={{ color: 'var(--theme-elevation-600)' }}
            >
              Current Score
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold" style={{ color: 'var(--theme-elevation-700)' }}>
              {maxScore}
            </div>
            <div
              className="mt-2 text-sm font-medium"
              style={{ color: 'var(--theme-elevation-600)' }}
            >
              Max Score
            </div>
          </div>
          <div className="text-center">
            <div
              className="text-4xl font-bold"
              style={{
                color: isPassed ? 'var(--theme-success-700)' : 'var(--theme-error-700)',
              }}
            >
              {maxScore > 0 ? Math.round((score / maxScore) * 100) : 0}%
            </div>
            <div
              className="mt-2 text-sm font-medium"
              style={{ color: 'var(--theme-elevation-600)' }}
            >
              Percentage
            </div>
          </div>
          <div className="text-center">
            <div
              className="text-4xl font-bold"
              style={{
                color: isPassed ? 'var(--theme-success-700)' : 'var(--theme-warning-700)',
              }}
            >
              {isPassed ? '✓' : '✗'}
            </div>
            <div
              className="mt-2 text-sm font-medium"
              style={{ color: 'var(--theme-elevation-600)' }}
            >
              {isPassed ? 'Passed' : 'Not Passed'} ({test.minimumPercentage}% needed)
            </div>
          </div>
        </div>
      </div>

      {/* Questions Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold" style={{ color: 'var(--theme-text)' }}>
            Questions & Answers
          </h3>
          <span className="text-sm font-medium" style={{ color: 'var(--theme-elevation-600)' }}>
            {totalQuestions} {totalQuestions === 1 ? 'Question' : 'Questions'}
          </span>
        </div>

        <div className="space-y-5">
          {test.questions.map((question, index) => {
            if (!question.id) return null

            const gradingDetail = gradingMap.get(question.id)
            const studentAnswer = (submission.answers as Record<string, AnswerObject>)[question.id]

            return (
              <QuestionGrader
                key={question.id}
                question={question}
                index={index}
                test={test}
                gradingDetail={gradingDetail}
                studentAnswer={studentAnswer}
                feedback={feedback[question.id]}
                onFeedbackChange={handleFeedbackChange}
                onManualGrade={handleManualGrade}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

// Payload Field Wrapper Component
export const TestSubmissionGraderField: React.FC = () => {
  const documentInfo = useDocumentInfo()
  const { setValue: setScore } = useField({ path: 'score' })
  const { setValue: setMaxScore } = useField({ path: 'maxScore' })
  const { setValue: setPercentageScore } = useField({ path: 'percentageScore' })
  const { setValue: setGradingDetails } = useField({ path: 'gradingDetails' })
  const { value: statusValue, setValue: setStatus } = useField({ path: 'status' })
  const { setValue: setPassed } = useField({ path: 'passed' }) // ✅ FIXED: Added passed field

  const [test, setTest] = useState<Test | null>(null)
  const [loading, setLoading] = useState(true)

  // Fetch test data when component mounts
  useEffect(() => {
    const fetchTest = async () => {
      if (!documentInfo?.id) return

      try {
        const submissionResponse = await fetch(`/api/test-submissions/${documentInfo.id}`)
        const submissionData = await submissionResponse.json()

        if (submissionData?.test) {
          const testId =
            typeof submissionData.test === 'object' ? submissionData.test.id : submissionData.test

          const testResponse = await fetch(`/api/tests/${testId}`)
          const testData = await testResponse.json()

          setTest(testData)
        }
      } catch (error) {
        console.error('Failed to fetch test data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTest()
  }, [documentInfo?.id])

  const handleScoreUpdate = (
    newScore: number,
    newMaxScore: number,
    newPercentageScore: number,
    gradingDetails: GradingDetail[],
    passed: boolean, // ✅ FIXED: Added passed parameter
  ) => {
    // Update all form field values
    setScore(newScore)
    setMaxScore(newMaxScore)
    setPercentageScore(newPercentageScore)
    setGradingDetails(gradingDetails)
    setPassed(passed) // ✅ FIXED: Update passed field

    // Update status to graded if all questions are graded
    const allQuestionsGraded = gradingDetails.every(
      (detail) =>
        detail.manuallyGraded === false ||
        (detail.manuallyGraded === true && detail.isCorrect !== null),
    )

    if (allQuestionsGraded && statusValue !== 'graded') {
      setStatus('graded')
    }
  }

  if (loading) {
    return (
      <div className="relative min-h-64">
        <LoadingOverlay />
        <div className="flex items-center justify-center p-8">
          <div className="text-[var(--theme-elevation-600)]">Loading test data...</div>
        </div>
      </div>
    )
  }

  if (!test) {
    return (
      <div className="p-4 border border-red-200 rounded bg-red-50 dark:bg-red-950/20 dark:border-red-800">
        <p className="text-red-800 dark:text-red-400">Unable to load test data for grading.</p>
      </div>
    )
  }

  const submission = documentInfo?.initialData as TestSubmission

  return (
    <div className="payload-field">
      {/* Test Description */}
      {test.description && (
        <div
          className="mb-6 p-4 border rounded-lg"
          style={{
            backgroundColor: 'var(--theme-elevation-50)',
            borderColor: 'var(--theme-elevation-200)',
          }}
        >
          <h3 className="mb-3 text-lg font-bold" style={{ color: 'var(--theme-text)' }}>
            Test Description:
          </h3>
          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            style={{ color: 'var(--theme-elevation-800)' }}
          >
            <RichText data={test.description} converters={richTextConverters} />
          </div>
        </div>
      )}
      <TestSubmissionGrader
        submission={submission}
        test={test}
        onScoreUpdate={handleScoreUpdate}
        submissionId={documentInfo?.id?.toString() || ''}
      />
    </div>
  )
}
